/**
 * Магазин: публичный и админский API.
 * Контракт — docs/worklog/2026-10-03-store-into-be.md.
 *
 * GET  /api/store/config                       → StoreConfig
 * GET  /api/store/products                     → StoreProduct[]
 * GET  /api/store/categories                   → StoreCategoryInfo[]
 * GET  /api/store/payment-methods              → StorePaymentMethod[]
 * GET  /api/store/products/{id}/quote?coins=N → StoreQuote (custom-товар)
 * POST /api/store/orders                       → CreateOrderResponse
 * GET  /api/store/orders/{id}  (X-Order-Token) → StoreOrder
 * GET  /api/store/orders/my?page&size          → Page<StoreOrder>
 * GET  /api/admin/store/payment-methods        → AdminStorePaymentMethod[]
 * PUT  /api/admin/store/payment-methods/{id}   → AdminStorePaymentMethod
 * GET  /api/admin/store/categories            → AdminStoreCategory[]
 * PUT  /api/admin/store/categories/{id}       → AdminStoreCategory
 * GET  /api/admin/store/tag-requests?status&page&size → Page<AdminTagRequest>   (ADMIN)
 * PUT  /api/admin/store/tag-requests/{id}      → правка заявки в pending-moderation
 * POST /api/admin/store/tag-requests/{id}/approve | /reject {comment}
 * Покупка тега: docs/worklog/2026-10-18-store-tag-purchase.md
 * /api/admin/store/**                          → админка (mark-paid требует STORE_ORDER_MARK_PAID)
 */

import api from './axios';

export type StoreCategory = 'host' | 'supporter' | 'currency' | 'tag';
export type StoreBadge = 'popular' | 'bestValue';
export type StoreOrderStatus = 'pending' | 'paid' | 'canceled';
export type StoreFulfillmentStatus = 'pending' | 'completed' | 'failed';
export type StoreEmailStatus = 'pending' | 'sent' | 'failed' | 'skipped';
export type StoreFulfillmentKind = 'host-key' | 'subscription' | 'promo-code' | 'tag';
export type StoreTagStatus = 'awaiting-payment' | 'pending-moderation' | 'approved' | 'rejected';
export type StoreTagRequestStatusFilter = Exclude<StoreTagStatus, 'awaiting-payment'>;

export const STORE_MARK_PAID_PERMISSION = 'STORE_ORDER_MARK_PAID';
export const FUNPAY_PAYMENT_METHOD_ID = 'funpay';

/** Срок подписки; первый в списке — базовый (months=1, цена товара). */
export interface StoreProductTerm {
  months: number;
  durationDays: number;
  price: number;
  usdPrice: number | null;
  /** Фактическая скидка к цене `price товара × months`, % */
  discountPercent: number;
  monthlyPrice: number;
  /** У срока есть лот FunPay: метод `funpay` для него доступен. */
  funpayAvailable: boolean;
}

export interface StoreProduct {
  id: string;
  category: StoreCategory;
  name: string;
  /** Пусто — перевода нет. */
  nameEn: string;
  description: string;
  descriptionEn: string;
  features: string[];
  featuresEn: string[];
  price: number;
  currency: string;
  usdPrice: number | null;
  durationDays: number | null;
  coins: number | null;
  badge: StoreBadge | null;
  featured: boolean;
  /** Относительный путь `/files/catalog/...`; в браузере требует префикс `/api`. */
  imageUrl: string | null;
  customAmount: boolean;
  customCoins: StoreCustomCoins | null;
  /** У товара задан лот FunPay: метод `funpay` для него доступен. */
  funpayAvailable: boolean;
  /** Пусто — выбора срока нет. */
  terms: StoreProductTerm[];
}

export interface StoreCategoryInfo {
  id: StoreCategory;
  titleRu: string;
  titleEn: string;
  descriptionRu: string;
  descriptionEn: string;
  sortOrder: number;
}

export interface AdminStoreCategory extends StoreCategoryInfo {
  updatedAt: string;
}

export type SaveStoreCategoryRequest = Omit<StoreCategoryInfo, 'id'>;

export interface StoreCustomCoins {
  min: number;
  max: number;
  step: number;
}

export interface StoreQuote {
  productId: string;
  coins: number;
  price: number;
  currency: string;
}

export interface StorePaymentMethod {
  id: string;
  displayName: string;
}

export interface AdminStorePaymentMethod extends StorePaymentMethod {
  configured: boolean;
  enabled: boolean;
  /** Метод видят только админы. */
  adminOnly: boolean;
  updatedAt: string | null;
}

export interface StoreConfig {
  accessKeyRequired: boolean;
}

/** Стиль тега в заказе; `iconPng` — base64 PNG 64x64 без `data:`-префикса. */
export interface StoreTagPayload {
  name: string;
  colors: string[];
  angle: number;
  iconPng: string | null;
  contact: string;
}

export interface StoreOrderTag {
  name: string;
  colors: string[];
  angle: number;
  /** Относительный путь `/files/catalog/tag-….png`. */
  iconUrl: string | null;
  contact: string;
  status: StoreTagStatus;
  moderatorComment: string | null;
}

export interface CreateOrderRequest {
  requestId: string;
  productId: string;
  email: string;
  uid?: string;
  accessKey?: string;
  paymentMethodId?: string;
  /** Обязателен для custom-товара, для остальных не отправляется. */
  coins?: number;
  /** Обязателен для товара категории tag, для остальных не отправляется. */
  tag?: StoreTagPayload;
  /** Срок подписки в месяцах; для базового срока не отправляется. */
  months?: number;
}

export interface CreateOrderResponse {
  orderId: string;
  accessToken: string;
  status: StoreOrderStatus;
  paymentUrl: string | null;
}

export interface StoreOrder {
  orderId: string;
  status: StoreOrderStatus;
  productId: string;
  productName: string;
  /** null — у старых заказов снапшота перевода нет. */
  productNameEn: string | null;
  category: StoreCategory;
  amount: number;
  currency: string;
  uid: string | null;
  fulfillmentStatus: StoreFulfillmentStatus;
  emailStatus: StoreEmailStatus;
  code: string | null;
  /** "yyyy-MM-dd" */
  subscriptionUntil: string | null;
  durationDays: number | null;
  coins: number | null;
  createdAt: string;
  paidAt: string | null;
  error: string | null;
  test: boolean;
  paymentUrl: string | null;
  paymentProvider: string | null;
  /** `RR-<invoiceId>` для заказов FunPay, иначе null. */
  paymentCode: string | null;
  tag?: StoreOrderTag | null;
}

export interface StoreOrdersPage {
  content: StoreOrder[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

/** Дополнительный срок подписки в настройках товара; базовый срок — сам товар. */
export interface AdminStoreProductTerm {
  months: number;
  /** null — скидка считается автоматически. */
  discountPercent: number | null;
  active: boolean;
  /** Цена и фактическая скидка, посчитанные BE; при сохранении не отправляются. */
  price?: number;
  effectiveDiscountPercent?: number;
  /** Лот FunPay срока; null — лота нет. */
  funpayLotId?: string | null;
  funpayLotTitle?: string | null;
}

export type SaveStoreProductTerm = Pick<
  AdminStoreProductTerm,
  'months' | 'discountPercent' | 'active' | 'funpayLotId' | 'funpayLotTitle'
>;

export interface AdminStoreProduct extends Omit<StoreProduct, 'terms'> {
  /** 0..50 */
  termMaxDiscountPercent: number;
  terms: AdminStoreProductTerm[];
  active: boolean;
  sortOrder: number;
  fulfillmentKind: StoreFulfillmentKind;
  fulfillmentValue: number;
  funpayLotId: string | null;
  funpayLotTitle: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SaveStoreProductRequest {
  name: string;
  nameEn: string;
  description: string;
  descriptionEn: string;
  features: string[];
  featuresEn: string[];
  price: number;
  usdPrice: number | null;
  currency: string;
  fulfillmentValue: number;
  badge: StoreBadge | null;
  featured: boolean;
  sortOrder: number;
  active: boolean;
  imageUrl: string | null;
  /** Только цифры, 1..20; null — лота нет. */
  funpayLotId: string | null;
  /** Название лота на FunPay, до 255 символов; null — не задано. */
  funpayLotTitle: string | null;
  /** 0..50; сроки допустимы только у категории supporter. */
  termMaxDiscountPercent: number;
  terms: SaveStoreProductTerm[];
}

export interface CreateStoreProductRequest extends SaveStoreProductRequest {
  id: string;
  category: StoreCategory;
}

export type StoreDisputeReason = 'dispute' | 'refund';

export type AdminStoreOrder = Omit<StoreOrder, 'code' | 'paymentUrl'> & {
  /** null у заказов FunPay, оформленных без сайта. */
  email: string | null;
  funpayOrderId: string | null;
  disputedAt: string | null;
  disputeReason: StoreDisputeReason | null;
  accountId: string | null;
  paidByName: string | null;
  paidComment: string | null;
  fulfillmentAttempts: number;
  updatedAt: string;
};

export interface AdminStoreOrdersPage {
  content: AdminStoreOrder[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export interface AdminStoreOrderFilters {
  status?: StoreOrderStatus;
  fulfillmentStatus?: StoreFulfillmentStatus;
  productId?: string;
  test?: boolean;
  query?: string;
  /** ISO instant, включительно */
  from?: string;
  /** ISO instant, исключительно */
  to?: string;
}

export interface AdminTagRequest {
  id: string;
  orderId: string;
  uid: string;
  playerName: string | null;
  name: string;
  colors: string[];
  angle: number;
  iconUrl: string | null;
  contact: string;
  /** Без фильтра BE отдаёт и неоплаченные заявки (awaiting-payment). */
  status: StoreTagStatus;
  tagCode: string | null;
  moderatorComment: string | null;
  createdAt: string;
  paidAt: string | null;
  moderatedAt: string | null;
  moderatedByName: string | null;
}

export interface AdminTagRequestsPage {
  content: AdminTagRequest[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

/** `iconPng: null` и `removeIcon: false` — иконка не меняется. */
export interface UpdateTagRequestBody {
  name: string;
  colors: string[];
  angle: number;
  iconPng: string | null;
  removeIcon: boolean;
}

export interface StoreAnalytics {
  paidCount: number;
  revenue: { currency: string; amount: number }[];
  byProduct: { productId: string; productName: string; count: number; amount: number }[];
  /** date — "yyyy-MM-dd" */
  byDay: { date: string; count: number; amount: number }[];
}

export const getStoreConfig = (): Promise<StoreConfig> =>
  api.get<StoreConfig>('/api/store/config').then(r => r.data);

export const listStoreProducts = (): Promise<StoreProduct[]> =>
  api.get<StoreProduct[]>('/api/store/products').then(r => r.data);

export const listStoreCategories = (): Promise<StoreCategoryInfo[]> =>
  api.get<StoreCategoryInfo[]>('/api/store/categories').then(r => r.data);

export const quoteStoreProduct = (productId: string, coins: number, signal?: AbortSignal): Promise<StoreQuote> =>
  api
    .get<StoreQuote>(`/api/store/products/${encodeURIComponent(productId)}/quote`, { params: { coins }, signal })
    .then(r => r.data);

export const listStorePaymentMethods = (): Promise<StorePaymentMethod[]> =>
  api.get<StorePaymentMethod[]>('/api/store/payment-methods').then(r => r.data);

export const createStoreOrder = (request: CreateOrderRequest): Promise<CreateOrderResponse> =>
  api.post<CreateOrderResponse>('/api/store/orders', request).then(r => r.data);

/** Без токена запрос работает только для владельца-аккаунта. */
export const getStoreOrder = (orderId: string, token?: string | null): Promise<StoreOrder> =>
  api
    .get<StoreOrder>(`/api/store/orders/${encodeURIComponent(orderId)}`, {
      headers: token ? { 'X-Order-Token': token } : undefined,
    })
    .then(r => r.data);

export const listMyStoreOrders = (page: number, size = 5): Promise<StoreOrdersPage> =>
  api.get<StoreOrdersPage>('/api/store/orders/my', { params: { page, size } }).then(r => r.data);

export const uploadStoreProductImage = (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);
  return api
    .post<{ url: string; fileName: string }>('/api/admin/uploads/catalog-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then(r => r.data.url);
};

export const listAdminStoreProducts = (): Promise<AdminStoreProduct[]> =>
  api.get<AdminStoreProduct[]>('/api/admin/store/products').then(r => r.data);

export const createAdminStoreProduct = (request: CreateStoreProductRequest): Promise<AdminStoreProduct> =>
  api.post<AdminStoreProduct>('/api/admin/store/products', request).then(r => r.data);

export const updateAdminStoreProduct = (id: string, request: SaveStoreProductRequest): Promise<AdminStoreProduct> =>
  api.put<AdminStoreProduct>(`/api/admin/store/products/${encodeURIComponent(id)}`, request).then(r => r.data);

export const listAdminStoreOrders = (
  filters: AdminStoreOrderFilters,
  page: number,
  size = 20,
): Promise<AdminStoreOrdersPage> =>
  api.get<AdminStoreOrdersPage>('/api/admin/store/orders', { params: { ...filters, page, size } }).then(r => r.data);

export const getAdminStoreAnalytics = (from?: string, to?: string, includeTest?: boolean): Promise<StoreAnalytics> =>
  api
    .get<StoreAnalytics>('/api/admin/store/analytics', { params: { from, to, includeTest: includeTest || undefined } })
    .then(r => r.data);

export const markAdminStoreOrderPaid = (orderId: string, comment: string): Promise<AdminStoreOrder> =>
  api.post<AdminStoreOrder>(`/api/admin/store/orders/${encodeURIComponent(orderId)}/mark-paid`, { comment }).then(r => r.data);

export const retryAdminStoreFulfillment = (orderId: string): Promise<AdminStoreOrder> =>
  api.post<AdminStoreOrder>(`/api/admin/store/orders/${encodeURIComponent(orderId)}/retry-fulfillment`).then(r => r.data);

export const cancelAdminStoreOrder = (orderId: string): Promise<AdminStoreOrder> =>
  api.post<AdminStoreOrder>(`/api/admin/store/orders/${encodeURIComponent(orderId)}/cancel`).then(r => r.data);

export const listAdminStorePaymentMethods = (): Promise<AdminStorePaymentMethod[]> =>
  api.get<AdminStorePaymentMethod[]>('/api/admin/store/payment-methods').then(r => r.data);

export const setAdminStorePaymentMethodEnabled = (id: string, enabled: boolean): Promise<AdminStorePaymentMethod> =>
  api.put<AdminStorePaymentMethod>(`/api/admin/store/payment-methods/${encodeURIComponent(id)}`, { enabled }).then(r => r.data);

export const listAdminStoreCategories = (): Promise<AdminStoreCategory[]> =>
  api.get<AdminStoreCategory[]>('/api/admin/store/categories').then(r => r.data);

export const updateAdminStoreCategory = (id: StoreCategory, request: SaveStoreCategoryRequest): Promise<AdminStoreCategory> =>
  api.put<AdminStoreCategory>(`/api/admin/store/categories/${encodeURIComponent(id)}`, request).then(r => r.data);

export const listAdminTagRequests = (
  status: StoreTagRequestStatusFilter | undefined,
  page: number,
  size = 20,
): Promise<AdminTagRequestsPage> =>
  api.get<AdminTagRequestsPage>('/api/admin/store/tag-requests', { params: { status, page, size } }).then(r => r.data);

export const updateAdminTagRequest = (id: string, body: UpdateTagRequestBody): Promise<AdminTagRequest> =>
  api.put<AdminTagRequest>(`/api/admin/store/tag-requests/${encodeURIComponent(id)}`, body).then(r => r.data);

export const approveAdminTagRequest = (id: string): Promise<AdminTagRequest> =>
  api.post<AdminTagRequest>(`/api/admin/store/tag-requests/${encodeURIComponent(id)}/approve`).then(r => r.data);

export const rejectAdminTagRequest = (id: string, comment: string): Promise<AdminTagRequest> =>
  api.post<AdminTagRequest>(`/api/admin/store/tag-requests/${encodeURIComponent(id)}/reject`, { comment }).then(r => r.data);
